/**
 * Login Page
 *
 * In production: POST to /api/v1/auth/login with email + password.
 * On success: store JWT, persist role + session lock, sync UI shell, redirect to /ops.
 *
 * Expected JSON (field names may vary; parser accepts common aliases):
 * - token (or jwt / data.token)
 * - role or user_type / user_role: "admin" | "practice" | "employer"
 * - practice_id (practice users) — UUID
 * - employer_id (employer users) — UUID
 *
 * Demo credentials (replace with real auth):
 *   admin@luminatherapyalliance.com  / any password → admin
 *   admin@wtgtherapy.com             / any password → practice p1
 *   admin@therapygroupdc.com         / any password → practice p7
 *   schen@meridiancap.com            / any password → employer e1
 */

import React, { useEffect, useState } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { C } from '../utils/constants'
import { Inp, Btn } from '../components/ui'
import api, { ACCOUNT_DEACTIVATED_MESSAGE, setToken } from '../utils/api'
import { SESSION_LOCK, useUiShellStore } from '../data/stores'

function pick(res, ...keys) {
  for (const k of keys) {
    if (res && res[k] !== undefined && res[k] !== null && res[k] !== '') return res[k]
    if (res?.data && res.data[k] !== undefined && res.data[k] !== null && res.data[k] !== '') return res.data[k]
  }
  return ''
}

export default function LoginPage() {
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const { applyPostLogin } = useUiShellStore()
  const [email, setEmail]     = useState('')
  const [password, setPassword] = useState('')
  const [error, setError]     = useState('')
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    if (searchParams.get('reason') === 'deactivated') {
      setError(ACCOUNT_DEACTIVATED_MESSAGE)
      setSearchParams({}, { replace: true })
    }
  }, [searchParams, setSearchParams])

  const handleLogin = async (e) => {
    e.preventDefault()
    setError('')
    setLoading(true)

    try {
      const res = await api.auth.login(email, password)
      const token = pick(res, 'token', 'jwt') || res?.data?.token
      const rawRole = String(
        pick(res, 'role', 'user_role', 'userRole', 'user_type', 'userType') || ''
      ).trim().toLowerCase()
      const practiceId = String(pick(res, 'practice_id', 'practiceId') || '').trim()
      const employerId = String(pick(res, 'employer_id', 'employerId') || '').trim()

      if (!token) throw new Error('Login response missing token.')

      let luminaRole = 'admin'
      let sessionLock = SESSION_LOCK.MULTI

      let kind = ['admin', 'practice', 'employer'].includes(rawRole) ? rawRole : null
      if (!kind && practiceId && !employerId) kind = 'practice'
      if (!kind && employerId && !practiceId) kind = 'employer'
      if (!kind) throw new Error('Login response missing or invalid role (expected admin, practice, or employer).')

      if (kind === 'admin') {
        luminaRole = 'admin'
        sessionLock = SESSION_LOCK.MULTI
      } else if (kind === 'practice') {
        if (!practiceId) throw new Error('Login response missing practice_id for practice users.')
        luminaRole = practiceId
        sessionLock = SESSION_LOCK.PRACTICE
      } else {
        if (!employerId) throw new Error('Login response missing employer_id for employer users.')
        luminaRole = employerId
        sessionLock = SESSION_LOCK.EMPLOYER
      }

      setToken(token)
      const nestedUser = res?.user && typeof res.user === 'object' ? res.user : null
      const userId = String(
        pick(res, 'user_id', 'userId') || (nestedUser ? pick(nestedUser, 'id') : '') || '',
      ).trim()
      if (userId) localStorage.setItem('lumina_user_id', userId)
      else localStorage.removeItem('lumina_user_id')
      applyPostLogin({ role: luminaRole, sessionLock })
      navigate('/ops')
    } catch (err) {
      setError(err?.message || 'Login failed. Check credentials and try again.')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div style={{ minHeight:'100vh', background:C.bgPage, display:'flex', alignItems:'center', justifyContent:'center', padding:20 }}>
      <div style={{ background:C.white, border:`1px solid ${C.border}`, borderRadius:6, width:'100%', maxWidth:400, overflow:'hidden', boxShadow:'0 8px 32px rgba(29,107,107,0.12)' }}>

        <div style={{ background:C.tealDark, padding:'24px 28px' }}>
          <div style={{ fontSize:10, color:'#A8D5D5', textTransform:'uppercase', letterSpacing:'0.1em', marginBottom:4 }}>Welcome to</div>
          <div style={{ fontSize:20, fontWeight:700, color:C.white }}>LUMINA</div>
          <div style={{ fontSize:11, color:'#A8D5D5', letterSpacing:'0.06em' }}>THERAPY ALLIANCE</div>
          <div style={{ fontSize:12, color:'#7ABCBC', marginTop:8 }}>Operations Platform</div>
        </div>

        <form onSubmit={handleLogin} style={{ padding:24 }}>
          <Inp label="Email" type="email" value={email} onChange={e=>setEmail(e.target.value)} placeholder="your@email.com" autoComplete="email"/>
          <Inp label="Password" type="password" value={password} onChange={e=>setPassword(e.target.value)} placeholder="••••••••" autoComplete="current-password"/>

          {error && (
            <div style={{ background:'#FCE8E8', border:'1px solid #D9534F', borderRadius:4, padding:'9px 12px', fontSize:12, color:'#B03A3A', marginBottom:14 }}>
              {error}
            </div>
          )}

          <Btn onClick={handleLogin} disabled={loading || !email || !password} style={{ width:'100%', marginTop:4 }}>
            {loading ? 'Signing in…' : 'Sign In'}
          </Btn>

          <div style={{ marginTop:14, textAlign:'right' }}>
            <Link to="/forgot-password" style={{ fontSize:12, color:C.teal, textDecoration:'none', fontWeight:600 }}>
              Forgot password?
            </Link>
          </div>

          <div style={{ marginTop:12, fontSize:11, color:C.textMid, lineHeight:1.7 }}>
            Sign in with your Lumina account credentials.
          </div>
        </form>
      </div>
    </div>
  )
}
